import { flowApi } from '.';
import { TAG_TYPES } from 'types/services';
import type {
  FormItemDependencyCleanupConfirmation,
  FormItemDependencyDetail,
  FormItemDependencySummaries,
  GetAgendasFormItemsReq,
  GetAgendasFormItemsRes,
  GetAgendasFormRes,
  TabSection as TabSectionReq,
} from 'types';

export const formService = flowApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getAgendasForms: builder.query<GetAgendasFormRes, { agendaId: string }>({
      query: ({ agendaId }) => ({
        url: `Agendas/${agendaId}/Form`,
        method: 'GET',
      }),
      providesTags: (_result, _error, { agendaId }) => [{ type: TAG_TYPES.AGENDAS, id: agendaId }],
    }),
    getAgendasFormItems: builder.query<GetAgendasFormItemsRes, GetAgendasFormItemsReq>({
      query: ({ agendaId }) => ({
        url: `Agendas/${agendaId}/FormItems`,
        method: 'GET',
      }),
      providesTags: (_result, _error, { agendaId }) => [{ type: TAG_TYPES.AGENDAS, id: agendaId }],
    }),
    getAgendasFormItemsDependencies: builder.query<FormItemDependencySummaries, GetAgendasFormItemsReq>({
      query: ({ agendaId }) => ({
        url: `Agendas/${agendaId}/FormItems/Dependencies`,
        method: 'GET',
      }),
      providesTags: (_result, _error, { agendaId }) => [{ type: TAG_TYPES.AGENDAS, id: agendaId }],
    }),
    getAgendasFormItemDependencies: builder.query<FormItemDependencyDetail, GetAgendasFormItemsReq & { formItemId: string }>({
      query: ({ agendaId, formItemId }) => ({
        url: `Agendas/${agendaId}/FormItems/${formItemId}/Dependencies`,
        method: 'GET',
      }),
      keepUnusedDataFor: 0,
    }),
    deleteAgendaFormItem: builder.mutation<
      void,
      GetAgendasFormItemsReq & {
        formItemId: string;
        confirmation: FormItemDependencyCleanupConfirmation;
        suppressErrorToast?: boolean;
      }
    >({
      query: ({ agendaId, formItemId, confirmation }) => ({
        url: `Agendas/${agendaId}/FormItems/${formItemId}`,
        method: 'DELETE',
        body: confirmation,
      }),
      invalidatesTags: (_result, _error, { agendaId }) => [
        { type: TAG_TYPES.AGENDAS, id: agendaId },
        { type: TAG_TYPES.CONDITIONS, id: 'DETAIL' },
        { type: TAG_TYPES.CONDITIONS, id: 'LIST' },
        TAG_TYPES.LOOKUPS,
        { type: TAG_TYPES.RECORDS, id: 'DETAIL' },
      ],
    }),
    updateOrCreateAgendaForm: builder.mutation<
      unknown,
      {
        agendaId: string;
        tabSections: TabSectionReq[];
        suppressErrorToastForStatus?: number;
      }
    >({
      query: ({ agendaId, tabSections }) => ({
        url: `Agendas/${agendaId}/Form`,
        method: 'PUT',
        body: { tabSections },
        headers: { 'x-api-version': '2.0' },
      }),
      invalidatesTags: (_result, _error, { agendaId }) => [
        { type: TAG_TYPES.AGENDAS, id: agendaId },
        { type: TAG_TYPES.CONDITIONS, id: 'DETAIL' },
        TAG_TYPES.LOOKUPS,
        { type: TAG_TYPES.RECORDS, id: 'DETAIL' },
      ],
    }),
  }),
});
export const {
  useDeleteAgendaFormItemMutation,
  useGetAgendasFormItemDependenciesQuery,
  useLazyGetAgendasFormItemDependenciesQuery,
  useGetAgendasFormItemsDependenciesQuery,
  useLazyGetAgendasFormItemsDependenciesQuery,
  useGetAgendasFormItemsQuery,
  useLazyGetAgendasFormItemsQuery,
  useGetAgendasFormsQuery,
  useLazyGetAgendasFormsQuery,
  useUpdateOrCreateAgendaFormMutation,
} = formService;
